import Layout from '@comp/layout'
import SolutionMeta from '@comp/solution/solution-meta'
import SolutionHero from '@comp/solution/solution-hero'
import SolutionPillars, { Pillar } from '@comp/solution/solution-pillars'
import PersonaRoutine, { Moment } from '@comp/persona/persona-routine'
import PersonaHelpLinks, { HelpLinkGroup } from '@comp/persona/persona-help-links'
import SolutionRelated, { RelatedItem } from '@comp/solution/solution-related'
import FinalCta from '@comp/home/final-cta'

interface PageContent {
  metaTitle: string
  metaDescription: string
  heroEyebrow: string
  heroTitle: [string, string]
  heroBody: string
  badges: string[]
  primaryCta: string
  secondaryCta: string
  routineEyebrow: string
  routineTitle: string
  routineBody: string
  moments: Moment[]
  pillarsEyebrow: string
  pillarsTitle: string
  pillarsBody: string
  pillars: Pillar[]
  helpEyebrow: string
  helpTitle: string
  helpBody: string
  helpGroups: HelpLinkGroup[]
  relatedEyebrow: string
  relatedTitle: string
  related: RelatedItem[]
}

const content: Record<'en' | 'tr', PageContent> = {
  en: {
    metaTitle: 'Monofor for end users',
    metaDescription:
      'Sign in once, approve from your phone, and recover your account without a ticket. Monofor stays out of your way until you need it.',
    heroEyebrow: 'For end users',
    heroTitle: ['One sign-in.', 'Every app you need.'],
    heroBody:
      'Sign in once, approve sign-ins from your phone, and recover your account without a ticket. Monofor is built to stay out of your day until you need it.',
    badges: ['One sign-in everywhere', 'Tap-to-approve from your phone', 'Self-service recovery'],
    primaryCta: 'Start free trial →',
    secondaryCta: 'Talk with our experts',
    routineEyebrow: 'A day with Monofor',
    routineTitle: 'What a Monofor day looks like.',
    routineBody:
      "Monofor's job is to disappear when things work, and show up fast when something needs your attention.",
    moments: [
      {
        time: 'Coffee',
        title: 'Sign in once, work from anywhere',
        body: 'You open your laptop, sign in to Monosign, and every app you need (email, chat, CRM, wiki) opens without another password prompt.',
      },
      {
        time: 'Lunch',
        title: 'Approve a sign-in from your phone',
        body: 'You try to log in from a new device. Monofor Identity buzzes on your phone with the location and app. One tap, and you are in.',
      },
      {
        time: 'New phone',
        title: 'Recover in two minutes, not two days',
        body: 'You lost your phone. The self-service recovery flow asks for a second factor, walks you through enrolling the new device, and you are back to work before your coffee goes cold.',
      },
    ],
    pillarsEyebrow: 'What you get',
    pillarsTitle: 'Four things you will actually feel.',
    pillarsBody:
      'The capabilities that touch your day, not the ones that live in the admin console.',
    pillars: [
      {
        title: 'One sign-in for everything at work',
        description:
          'You sign in to Monosign once. Email, chat, the CRM, the wiki, the VPN all just open. No password gymnastics.',
        bullets: [
          'One identity across every work app',
          'Auto-fill on browser apps that need it',
          'Remember device for trusted networks',
          'Sign-in works from any browser',
        ],
      },
      {
        title: 'The Monofor Identity app',
        description:
          'A one-tap approval for sign-ins, a six-digit OTP when you are offline, and biometric unlock when your face or fingerprint is faster than typing.',
        bullets: [
          'Tap to approve a sign-in',
          'Offline six-digit OTP codes',
          'Biometric unlock (Face ID / Touch ID)',
          'QR-code desktop login',
        ],
      },
      {
        title: 'Reset your own password, without a ticket',
        description:
          'Forgot your password? Lost your phone? Self-service flows recover most situations in under two minutes; help-desk-assisted recovery handles the rest.',
        bullets: [
          'Self-service password reset',
          'MFA challenge to prove identity',
          'Help-desk-assisted fallback',
          'Status updates so you know it worked',
        ],
      },
      {
        title: 'Built so you barely notice it',
        description:
          'When everything is working, you should not have to think about identity. Monofor is engineered to stay out of your day.',
        bullets: [
          'Trusted-device remembers you',
          'Step-up only when something looks risky',
          'Clean, accessible sign-in screen',
          'Available in EN, TR, AR, and more',
        ],
      },
    ],
    helpEyebrow: 'Straight to the docs',
    helpTitle: 'Help when you need it.',
    helpBody:
      'Quick-jump links into the help center for the things end users ask most.',
    helpGroups: [
      {
        title: 'Getting started',
        links: [
          { label: 'Configure Monofor Identity', description: 'Set up the mobile authenticator', href: 'https://help.monofor.com/kb/how-to-configure-monosign-authenticator' },
          { label: 'Monofor Identity supported platforms', href: 'https://help.monofor.com/monosign/monofor-identity-supported-platforms' },
          { label: 'Authenticator details', href: 'https://help.monofor.com/monosign/authenticator-details' },
          { label: 'Update your profile photo', href: 'https://help.monofor.com/monosign/how-to-update-user-profile-photo' },
        ],
      },
      {
        title: 'Account recovery',
        links: [
          { label: 'Unlock an account or recover password', href: 'https://help.monofor.com/monosign/how-to-unlock-account-recover-password' },
          { label: 'Reset users password (help-desk)', href: 'https://help.monofor.com/monosign/reset-users-password' },
          { label: 'Troubleshooting on multiple devices', href: 'https://help.monofor.com/monosign/troubleshooting-on-multiple-devices' },
          { label: 'Notification (SMS) troubleshoot', href: 'https://help.monofor.com/kb/monosign-notification-sms-troubleshoot' },
        ],
      },
      {
        title: 'Sign-in surfaces',
        links: [
          { label: 'Browser-based sign-in', href: 'https://help.monofor.com/monosign/browser-based-sign-in' },
          { label: 'Windows passwordless login', href: 'https://help.monofor.com/monosign/windows-passwordless-login' },
          { label: 'macOS passwordless login', href: 'https://help.monofor.com/monosign/macos-passwordless-login' },
          { label: 'Install Monofor Identity Client (Windows)', href: 'https://help.monofor.com/monosign/installing-monofor-identity-client-windows' },
        ],
      },
      {
        title: 'Troubleshooting',
        links: [
          { label: 'Troubleshooting overview', href: 'https://help.monofor.com/monosign/troubleshooting' },
          { label: 'Windows passwordless login issues', href: 'https://help.monofor.com/monosign/troubleshooting-on-windows-passwordless-login' },
          { label: 'Monosign FAQ', href: 'https://help.monofor.com/monosign/faq' },
          { label: 'Uninstall Monofor Identity Client (Windows)', href: 'https://help.monofor.com/monosign/uninstall-monofor-identity-client-windows' },
        ],
      },
    ],
    relatedEyebrow: 'Keep exploring',
    relatedTitle: 'Related Monofor capabilities.',
    related: [
      { label: 'Monofor Identity', description: 'The branded authenticator app.', href: '/sign/authenticator' },
      { label: 'Multi-Factor Authentication', description: 'The factors behind every sign-in.', href: '/sign/mfa' },
      { label: 'Passwordless & MFA', description: 'Use case: retiring passwords for good.', href: '/solutions/passwordless-mfa' },
    ],
  },
  tr: {
    metaTitle: 'Son kullanıcılar için Monofor',
    metaDescription:
      'Bir kez oturum açın, telefonunuzdan onaylayın ve hesabınızı talep kaydı olmadan kurtarın. Monofor, ihtiyaç duyduğunuz ana kadar yolunuzdan çekilir.',
    heroEyebrow: 'Son kullanıcılar için',
    heroTitle: ['Tek oturum açma.', 'İhtiyacınız olan her uygulama.'],
    heroBody:
      'Bir kez oturum açın, girişleri telefonunuzdan onaylayın ve hesabınızı talep kaydı olmadan kurtarın. Monofor, ihtiyaç duyduğunuz ana kadar gününüze karışmamak için tasarlandı.',
    badges: ['Her yerde tek oturum açma', 'Telefondan tek dokunuşla onay', 'Self servis kurtarma'],
    primaryCta: 'Ücretsiz deneme başlat →',
    secondaryCta: 'Uzmanlarımızla görüşün',
    routineEyebrow: 'Monofor ile bir gün',
    routineTitle: 'Monofor’lu bir gün nasıl görünür.',
    routineBody:
      'Monofor’un işi, her şey yolundayken görünmez olmak; bir şey ilginizi gerektirdiğinde ise hızla ortaya çıkmak.',
    moments: [
      {
        time: 'Kahve',
        title: 'Bir kez oturum açın, her yerden çalışın',
        body: 'Dizüstünüzü açıyor, Monosign’a giriş yapıyorsunuz ve ihtiyacınız olan her uygulama (e-posta, sohbet, CRM, wiki) başka bir parola sormadan açılıyor.',
      },
      {
        time: 'Öğle',
        title: 'Girişi telefonunuzdan onaylayın',
        body: 'Yeni bir cihazdan giriş yapmayı deniyorsunuz. Monofor Identity, konum ve uygulama bilgisiyle telefonunuzda titriyor. Tek dokunuşla içeridesiniz.',
      },
      {
        time: 'Yeni telefon',
        title: 'İki günde değil, iki dakikada kurtarın',
        body: 'Telefonunuzu kaybettiniz. Self servis kurtarma akışı ikinci bir faktör istiyor, yeni cihazı kaydetmenizde size yol gösteriyor ve kahveniz soğumadan işinize dönüyorsunuz.',
      },
    ],
    pillarsEyebrow: 'Neler elde edersiniz',
    pillarsTitle: 'Gerçekten hissedeceğiniz dört şey.',
    pillarsBody:
      'Yönetim konsolunda yaşayanlar değil, gününüze dokunan yetenekler.',
    pillars: [
      {
        title: 'İşteki her şey için tek oturum açma',
        description:
          'Monosign’a bir kez giriş yaparsınız. E-posta, sohbet, CRM, wiki, VPN; hepsi doğrudan açılır. Parola cambazlığı yok.',
        bullets: [
          'Tüm iş uygulamalarında tek kimlik',
          'İhtiyaç duyan tarayıcı uygulamalarında otomatik doldurma',
          'Güvenilir ağlarda cihazı hatırlama',
          'Her tarayıcıdan çalışan oturum açma',
        ],
      },
      {
        title: 'Monofor Identity uygulaması',
        description:
          'Girişler için tek dokunuşla onay, çevrimdışıyken altı haneli OTP ve yüzünüz ya da parmak iziniz yazmaktan hızlı olduğunda biyometrik kilit açma.',
        bullets: [
          'Girişi tek dokunuşla onaylayın',
          'Çevrimdışı altı haneli OTP kodları',
          'Biyometrik kilit açma (Face ID / Touch ID)',
          'QR kodla masaüstü girişi',
        ],
      },
      {
        title: 'Parolanızı talep kaydı olmadan kendiniz sıfırlayın',
        description:
          'Parolanızı mı unuttunuz? Telefonunuzu mu kaybettiniz? Self servis akışlar çoğu durumu iki dakikadan kısa sürede çözer; kalanını yardım masası destekli kurtarma halleder.',
        bullets: [
          'Self servis parola sıfırlama',
          'Kimliğinizi kanıtlayan MFA doğrulaması',
          'Yardım masası destekli yedek akış',
          'İşlemin sonucunu gösteren durum bildirimleri',
        ],
      },
      {
        title: 'Neredeyse fark etmeyeceğiniz şekilde tasarlandı',
        description:
          'Her şey çalışırken kimliği düşünmek zorunda kalmamalısınız. Monofor, gününüzün dışında kalmak için tasarlandı.',
        bullets: [
          'Güvenilir cihaz sizi hatırlar',
          'Yalnızca riskli görünen durumlarda ek doğrulama',
          'Temiz, erişilebilir oturum açma ekranı',
          'EN, TR, AR ve daha fazla dilde',
        ],
      },
    ],
    helpEyebrow: 'Doğrudan dokümantasyona',
    helpTitle: 'İhtiyaç anında yardım.',
    helpBody:
      'Son kullanıcıların en çok sorduğu konular için yardım merkezine hızlı bağlantılar.',
    helpGroups: [
      {
        title: 'Başlarken',
        links: [
          { label: 'Monofor Identity’yi yapılandırma', description: 'Mobil doğrulayıcıyı kurun', href: 'https://help.monofor.com/kb/how-to-configure-monosign-authenticator' },
          { label: 'Monofor Identity desteklenen platformlar', href: 'https://help.monofor.com/monosign/monofor-identity-supported-platforms' },
          { label: 'Doğrulayıcı ayrıntıları', href: 'https://help.monofor.com/monosign/authenticator-details' },
          { label: 'Profil fotoğrafınızı güncelleme', href: 'https://help.monofor.com/monosign/how-to-update-user-profile-photo' },
        ],
      },
      {
        title: 'Hesap kurtarma',
        links: [
          { label: 'Hesap kilidini açma veya parola kurtarma', href: 'https://help.monofor.com/monosign/how-to-unlock-account-recover-password' },
          { label: 'Kullanıcı parolası sıfırlama (yardım masası)', href: 'https://help.monofor.com/monosign/reset-users-password' },
          { label: 'Birden çok cihazda sorun giderme', href: 'https://help.monofor.com/monosign/troubleshooting-on-multiple-devices' },
          { label: 'Bildirim (SMS) sorun giderme', href: 'https://help.monofor.com/kb/monosign-notification-sms-troubleshoot' },
        ],
      },
      {
        title: 'Oturum açma yüzeyleri',
        links: [
          { label: 'Tarayıcı tabanlı oturum açma', href: 'https://help.monofor.com/monosign/browser-based-sign-in' },
          { label: 'Windows parolasız oturum açma', href: 'https://help.monofor.com/monosign/windows-passwordless-login' },
          { label: 'macOS parolasız oturum açma', href: 'https://help.monofor.com/monosign/macos-passwordless-login' },
          { label: 'Monofor Identity Client kurulumu (Windows)', href: 'https://help.monofor.com/monosign/installing-monofor-identity-client-windows' },
        ],
      },
      {
        title: 'Sorun giderme',
        links: [
          { label: 'Sorun gidermeye genel bakış', href: 'https://help.monofor.com/monosign/troubleshooting' },
          { label: 'Windows parolasız oturum açma sorunları', href: 'https://help.monofor.com/monosign/troubleshooting-on-windows-passwordless-login' },
          { label: 'Monosign SSS', href: 'https://help.monofor.com/monosign/faq' },
          { label: 'Monofor Identity Client kaldırma (Windows)', href: 'https://help.monofor.com/monosign/uninstall-monofor-identity-client-windows' },
        ],
      },
    ],
    relatedEyebrow: 'Keşfetmeye devam edin',
    relatedTitle: 'İlgili Monofor yetenekleri.',
    related: [
      { label: 'Monofor Identity', description: 'Markalı doğrulayıcı uygulama.', href: '/sign/authenticator' },
      { label: 'Çok Faktörlü Kimlik Doğrulama', description: 'Her oturum açmanın arkasındaki faktörler.', href: '/sign/mfa' },
      { label: 'Parolasız ve MFA', description: 'Kullanım senaryosu: parolaları kalıcı olarak emekliye ayırmak.', href: '/solutions/passwordless-mfa' },
    ],
  },
}

type Props = { locale: string }

export default function EndUsersPage({ locale }: Props) {
  const c = locale === 'tr' ? content.tr : content.en
  return (
    <Layout>
      <SolutionMeta
        title={c.metaTitle}
        description={c.metaDescription}
        path="/for/end-users"
        locale={locale}
        bilingual
      />
      <main className="bg-surface text-ink-primary">
        <SolutionHero
          eyebrow={c.heroEyebrow}
          title={
            <>
              {c.heroTitle[0]}
              <br />
              {c.heroTitle[1]}
            </>
          }
          body={c.heroBody}
          primaryCta={{ label: c.primaryCta, href: '/try/now' }}
          secondaryCta={{ label: c.secondaryCta, href: '/contact' }}
          badges={c.badges}
        />
        <PersonaRoutine
          eyebrow={c.routineEyebrow}
          title={c.routineTitle}
          body={c.routineBody}
          moments={c.moments}
        />
        <SolutionPillars
          eyebrow={c.pillarsEyebrow}
          title={c.pillarsTitle}
          body={c.pillarsBody}
          pillars={c.pillars}
        />
        <PersonaHelpLinks
          eyebrow={c.helpEyebrow}
          title={c.helpTitle}
          body={c.helpBody}
          groups={c.helpGroups}
        />
        <SolutionRelated
          eyebrow={c.relatedEyebrow}
          title={c.relatedTitle}
          items={c.related}
        />
        <FinalCta />
      </main>
    </Layout>
  )
}

export async function getStaticProps({ locale }: { locale: string }) {
  return { props: { locale: locale ?? 'en' } }
}
